import { useQuery } from '@tanstack/react-query';
import { adminService } from '@/services/admin.service';
import { ArrowLeft, Database, Clock, DollarSign, TrendingUp, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export function AnalyticsCache() {
  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics', '30d'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        return await adminService.getCostAnalytics(token);
      } catch (error) {
        console.error('Failed to fetch cache analytics:', error);
        return null;
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return <div>No analytics data available</div>;
  }

  const { cache_metrics } = analytics;
  const { overall_stats, performance, cost_impact, cache_by_type } = cache_metrics;

  // Prepare data for charts
  const hitMissData = [
    { name: 'Cache Hits', value: overall_stats.cache_hits, color: '#10B981' },
    { name: 'Cache Misses', value: overall_stats.cache_misses, color: '#EF4444' },
  ];

  const cacheTypeData = cache_by_type.map((item: any) => ({
    name: item.type,
    hits: item.hits,
    misses: item.misses,
    hit_rate: item.hit_rate,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/analytics"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cache Analytics</h1>
            <p className="text-gray-600">Cache performance, efficiency, and cost impact</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
            <option>Last 30 days</option>
            <option>Last 7 days</option>
            <option>Last 90 days</option>
            <option>All time</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cache Hit Rate</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {overall_stats.hit_rate.toFixed(1)}%
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {overall_stats.cache_hits.toLocaleString()} hits
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Time Saved</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {performance.time_saved_hours.toFixed(1)}h
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Avg {performance.avg_cache_hit_time_ms}ms vs {performance.avg_cache_miss_time_ms}ms
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cost Savings</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                ${cost_impact.savings.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {cost_impact.savings_percent.toFixed(1)}% reduction
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {overall_stats.total_requests.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-fk-orange-vibrant100 rounded-lg">
              <Database className="w-6 h-6 text-#ea7500" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {overall_stats.cache_misses.toLocaleString()} misses
          </p>
        </div>
      </div>

      {/* Cache Performance Impact Banner */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              Cache Performance Impact
            </h3>
            <p className="text-green-700 mb-4">
              Caching is saving {performance.time_saved_hours.toFixed(1)} hours of compute time and
              ${cost_impact.savings.toFixed(2)} in LLM costs per month
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-green-600 font-medium">Cost Without Cache</p>
                <p className="text-2xl font-bold text-green-900">
                  ${cost_impact.estimated_cost_without_cache.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Actual Cost</p>
                <p className="text-2xl font-bold text-green-900">
                  ${cost_impact.cost_with_cache.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Savings</p>
                <p className="text-2xl font-bold text-green-900">
                  ${cost_impact.savings.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Efficiency Gain</p>
                <p className="text-2xl font-bold text-green-900">
                  {cost_impact.savings_percent.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cache Hit/Miss Distribution & Performance by Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hit/Miss Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Cache Hit/Miss Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={hitMissData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value, percent }) =>
                  `${name}: ${value.toLocaleString()} (${(percent * 100).toFixed(1)}%)`
                }
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {hitMissData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm font-medium text-gray-700">Cache Hits</span>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  {overall_stats.cache_hits.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">
                  {overall_stats.hit_rate.toFixed(1)}% of requests
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-sm font-medium text-gray-700">Cache Misses</span>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  {overall_stats.cache_misses.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">
                  {overall_stats.miss_rate.toFixed(1)}% of requests
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cache Performance by Type */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Cache Performance by Type
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={cacheTypeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="hits" fill="#10B981" name="Hits" stackId="a" />
              <Bar dataKey="misses" fill="#EF4444" name="Misses" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Comparison */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Response Time Comparison</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-green-50 rounded-lg border-2 border-green-200">
            <Clock className="w-8 h-8 text-green-600 mx-auto mb-3" />
            <p className="text-sm text-gray-600 mb-2">Cache Hit</p>
            <p className="text-3xl font-bold text-green-600">
              {performance.avg_cache_hit_time_ms}ms
            </p>
            <p className="text-xs text-gray-500 mt-2">Average response time</p>
          </div>

          <div className="text-center p-6 bg-red-50 rounded-lg border-2 border-red-200">
            <Clock className="w-8 h-8 text-red-600 mx-auto mb-3" />
            <p className="text-sm text-gray-600 mb-2">Cache Miss</p>
            <p className="text-3xl font-bold text-red-600">
              {performance.avg_cache_miss_time_ms}ms
            </p>
            <p className="text-xs text-gray-500 mt-2">Average response time</p>
          </div>

          <div className="text-center p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
            <Zap className="w-8 h-8 text-blue-600 mx-auto mb-3" />
            <p className="text-sm text-gray-600 mb-2">Speed Improvement</p>
            <p className="text-3xl font-bold text-blue-600">
              {((performance.avg_cache_miss_time_ms - performance.avg_cache_hit_time_ms) /
                performance.avg_cache_miss_time_ms *
                100).toFixed(0)}
              x
            </p>
            <p className="text-xs text-gray-500 mt-2">Faster with cache</p>
          </div>
        </div>
      </div>

      {/* Detailed Cache Statistics by Type */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Detailed Cache Statistics by Operation Type
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Operation Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cache Hits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cache Misses
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Requests
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hit Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cache_by_type.map((item: any) => (
                <tr key={item.type}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                    {item.hits.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-semibold">
                    {item.misses.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {(item.hits + item.misses).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1 mr-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              item.hit_rate >= 75
                                ? 'bg-green-500'
                                : item.hit_rate >= 50
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                            }`}
                            style={{ width: `${item.hit_rate}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">
                        {item.hit_rate.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Time Savings Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Time Savings Breakdown</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-700">Total Time Saved</p>
              <p className="text-xs text-gray-500 mt-1">
                Based on {overall_stats.cache_hits.toLocaleString()} cache hits
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">
                {performance.time_saved_hours.toFixed(1)} hours
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {(performance.time_saved_hours * 60).toFixed(0)} minutes
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-700">Avg Time Saved per Hit</p>
              <p className="text-xs text-gray-500 mt-1">
                Cache hit: {performance.avg_cache_hit_time_ms}ms vs Miss:{' '}
                {performance.avg_cache_miss_time_ms}ms
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">
                {(performance.avg_cache_miss_time_ms - performance.avg_cache_hit_time_ms).toFixed(
                  0
                )}
                ms
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {(
                  ((performance.avg_cache_miss_time_ms - performance.avg_cache_hit_time_ms) /
                    performance.avg_cache_miss_time_ms) *
                  100
                ).toFixed(1)}
                % faster
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          <Zap className="w-5 h-5 inline mr-2" />
          Cache Performance Insights
        </h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Excellent cache hit rate of {overall_stats.hit_rate.toFixed(1)}% - your caching strategy is highly effective
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Cache is reducing response times by {((performance.avg_cache_miss_time_ms - performance.avg_cache_hit_time_ms) / performance.avg_cache_miss_time_ms * 100).toFixed(0)}x on average
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Quality Scoring has the lowest hit rate ({cache_by_type.find((t: any) => t.type === 'Quality Scoring')?.hit_rate.toFixed(1)}%) - consider increasing cache TTL for this operation
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Total savings of ${cost_impact.savings.toFixed(2)}/month ({cost_impact.savings_percent.toFixed(1)}%) demonstrates strong ROI on caching infrastructure
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
